// The lattice the wind arrows sit on, and the arithmetic that keeps it even.
//
// Wind is the one variable a colour wash cannot tell the truth about: it is a
// vector, and hue can only carry its length. So ws is drawn as arrows instead —
// rotation for direction, length for speed — which turns "where do the samples
// go?" into a real question. The field never had to answer it: isobands are cut
// from the grid's own nodes, because marching squares needs a node *mesh*
// matching the grid topology (see build-grids.ts and isobands.ts).
//
// Arrows have no such constraint. sampleAt() reads any arbitrary lon/lat, so the
// lattice is free to live in the space where "evenly spaced on screen" is true by
// construction: Web Mercator. That is the whole idea here — the arithmetic below
// is what makes the spacing even at every latitude and every zoom, and what stops
// the arrows crawling when you drag the map.
//
// Pure: no I/O, no MapLibre. The server samples it (tiles.ts), the tests check it.

// Distance between neighbouring arrows on screen, in CSS px. ~44 gives a 30px
// arrow room to breathe without the field reading as sparse.
export const ARROW_PX = 44;

// MapLibre GL JS puts 512 px in the world at zoom 0, not 256. The 256 convention
// belongs to raster sources that declare tileSize:256 — this project has exactly
// one, the DEM (app.ts). Getting this wrong halves every arrow's spacing.
const WORLD_PX_Z0 = 512;

export const MERCATOR_MAX_LAT = 85.0511287798;
export const clampLat = (lat: number): number => Math.max(-MERCATOR_MAX_LAT, Math.min(MERCATOR_MAX_LAT, lat));

// Latitude <-> normalized Mercator y (0 at the north edge, 1 at the south).
export function normY(lat: number): number {
  const s = Math.sin(clampLat(lat) * (Math.PI / 180));
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
}
export function invNormY(y: number): number {
  const n = Math.PI * (1 - 2 * y);
  return (180 / Math.PI) * Math.atan(Math.sinh(n));
}

// Candidate spacings, in degrees.
//
// Every rung divides 360 exactly (asserted in wind-grid.test.ts), and that is the
// load-bearing property rather than tidiness: the column lattice is periodic, so a
// step that does not divide 360 cannot close at the antimeridian — nodes reached
// going east and nodes reached going west land on different meridians, and the
// arrow field gets a seam down the middle of the Pacific.
//
// Rungs are roughly a factor of sqrt(2) apart. That is what holds the on-screen
// spacing to about +/-10%; an octave ladder would let it breathe by 2x across
// every zoom level.
const STEP_LADDER = [
  0.125, 0.25, 0.375, 0.5, 0.75, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 9, 10, 12, 15,
  18, 20, 24, 30, 36, 40, 45, 60, 72, 90, 120,
];

// Ceiling on how many nodes a single request may enumerate. Normal viewports come
// nowhere near it (a 1400x900 view tops out around 780 by construction); this is
// here so a nonsense z + bbox pair cannot ask for half a million.
export const MAX_ARROWS = 4000;

// The lattice has to be a function of zoom alone — that is exactly what makes it
// pan-stable — so it must re-snap at some granularity. Half-steps cost one extra
// re-snap per octave and buy spacing that never drifts far from ARROW_PX.
export const quantizeZoom = (z: number): number => Math.round(Math.max(0, Math.min(24, z)) * 2) / 2;

// Degrees between arrows at this zoom: the ladder rung nearest the ideal spacing.
// Nearest in log space, so being 20% too coarse and 20% too fine count the same.
export function lonStepForZoom(z: number): number {
  const target = (ARROW_PX * 360) / (WORLD_PX_Z0 * Math.pow(2, quantizeZoom(z)));
  let best = STEP_LADDER[0];
  let bestErr = Infinity;
  for (const s of STEP_LADDER) {
    const err = Math.abs(Math.log(s / target));
    if (err < bestErr) {
      bestErr = err;
      best = s;
    }
  }
  return best;
}

// Meridians of the global lattice inside a west..east span, normalized to one
// world copy.
//
// A map bounds box is not a plain numeric interval: with world copies on, the
// client sends spans that cross the antimeridian (west 170, east -170) or run
// outside +/-180 entirely (west -190). Both are ordinary views — panning across
// the Pacific produces them. Only one copy of each meridian is ever emitted;
// MapLibre repeats a GeoJSON source across the visible world copies itself.
function latticeCols(west: number, east: number, step: number): number[] {
  const out: number[] = [];
  if (east - west >= 360) {
    // Whole world in view: exactly one full period, no duplicate at +/-180.
    for (let i = 0; i < Math.round(360 / step); i++) out.push(-180 + i * step);
    return out;
  }
  if (east < west) east += 360; // span wraps the antimeridian
  const i0 = Math.ceil((west + 180) / step - 1e-9);
  const i1 = Math.floor((east + 180) / step + 1e-9);
  for (let i = i0; i <= i1; i++) {
    const lon = -180 + i * step;
    out.push(((((lon + 180) % 360) + 360) % 360) - 180);
  }
  return out;
}

// Parallels of the global lattice, laid out in normalized Mercator y rather than
// in latitude.
//
// Mercator stretches y by 1/cos(lat), so rows every N degrees would drift APART
// on screen toward the poles — a lattice that is square at the equator and
// stringy over Norway. Stepping by the same fraction of the world in yNorm as the
// columns take in longitude makes it square at every latitude by construction,
// and makes the world exactly 360/step rows tall to match its 360/step columns.
function latticeRows(south: number, north: number, step: number): number[] {
  const dy = step / 360;
  const yTop = normY(Math.max(south, north)); // yNorm grows southward
  const yBot = normY(Math.min(south, north));
  const k0 = Math.max(0, Math.ceil(yTop / dy - 1e-9));
  const k1 = Math.min(Math.floor(1 / dy), Math.floor(yBot / dy + 1e-9));
  const out: number[] = [];
  for (let k = k0; k <= k1; k++) out.push(invNormY(k * dy));
  return out;
}

// The lattice nodes inside a viewport: the chosen ladder rung plus the meridians
// (cols, lon) and parallels (rows, lat) it puts inside the requested bbox.
export interface ArrowLattice {
  step: number;
  cols: number[];
  rows: number[];
}

// The lattice nodes inside a viewport: { step, cols (lon), rows (lat) }.
//
// Nodes are anchored to the world, not to the viewport — the step depends only on
// zoom, so panning slides the window over a fixed lattice instead of dragging the
// arrows along with it. Arrows that crawl under the cursor read as noise; arrows
// that stay put read as a field.
export function arrowLattice(
  west: number,
  south: number,
  east: number,
  north: number,
  z: number,
  maxNodes = MAX_ARROWS,
): ArrowLattice {
  let step = lonStepForZoom(z);
  let cols = latticeCols(west, east, step);
  let rows = latticeRows(south, north, step);
  // Degrade rather than explode. z and bbox are independent inputs, so nothing
  // stops a caller asking for the whole world at z=24. Walking to a coarser rung
  // answers with a correct-but-sparser lattice; truncating would just look broken.
  while (cols.length * rows.length > maxNodes) {
    const next: number | undefined = STEP_LADDER[STEP_LADDER.indexOf(step) + 1];
    if (next === undefined) break; // coarsest rung is 120 deg: 3x3 worldwide
    step = next;
    cols = latticeCols(west, east, step);
    rows = latticeRows(south, north, step);
  }
  return { step, cols, rows };
}

// Recover the meteorological direction (degrees the wind blows FROM) from the
// unit vector build-grids.ts bakes.
//
// The vector exists because direction is circular: a linear mean of two bearings
// either side of north points due south. Wrapped with a double modulo rather than
// `if (deg < 0) deg += 360`, because a due-north average is where that breaks —
// the sines cancel to a hair below zero rather than to zero, and the naive wrap
// answers 360° for a wind plainly at 0°. Both cases are pinned in wind-grid.test.ts.
export function windDirDeg(sin: number, cos: number): number {
  const deg = (Math.atan2(sin, cos) * 180) / Math.PI;
  return ((deg % 360) + 360) % 360;
}

// The 8-point compass index for a summed wind vector, or null when it summed to
// nothing at all. windDirDeg gives the FROM bearing the map's arrows use, so a
// briefing's "prevailing SW" and an arrow over the same city point the same way.
//
// Shared rather than inlined because a city's prevailing direction is now
// computed in two places — baked into the series table, and worked out live on
// the grid-sweep fallback — and those two must bin identically or the same city
// reads differently depending on whether the table was built.
export function prevailingOctant(sinSum: number, cosSum: number): number | null {
  if (sinSum === 0 && cosSum === 0) return null;
  return Math.round(windDirDeg(sinSum, cosSum) / 45) % 8;
}
